/**
 * How often a tiffin comes: daily, bi-weekly, weekly or at weekends — only
 * the schedules the kitchen offers, each with the days it means from the
 * chosen start ("Tuesdays & Fridays"), so "bi-weekly" is never read as
 * "every other week".
 */

import { memo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { scheduleDays, serviceScheduleLabel, type ServiceSchedule } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';


export interface SchedulePickerProps {
  offered: readonly ServiceSchedule[];
  /** The first day, which sets the weekdays of a weekly or bi-weekly plan. */
  start: string;
  value: ServiceSchedule | null;
  onChange: (schedule: ServiceSchedule) => void;
}

/** "Tuesdays & Fridays", "Tuesdays", "Every day". */
export function useScheduleDays() {
  const t = useT();
  return (schedule: ServiceSchedule, start: string) => {
    const [, ...weekdays] = scheduleDays(schedule, start);
    return weekdays.length ? weekdays.map((day) => t(day)).join(' & ') : t('Every day');
  };
}

export const SchedulePicker = memo(function SchedulePicker({
  offered,
  start,
  value,
  onChange,
}: SchedulePickerProps) {
  const t = useT();
  const days = useScheduleDays();

  return (
    <View style={styles.grid} accessibilityRole="radiogroup" testID="schedule-picker">
      {offered.map((schedule) => {
        const selected = value === schedule;
        const when = days(schedule, start);
        return (
          <Pressable
            key={schedule}
            testID={`schedule-${schedule}`}
            onPress={() => onChange(schedule)}
            accessibilityRole="radio"
            accessibilityState={{ selected, checked: selected }}
            // The web build reads only aria-* for state.
            aria-checked={selected}
            accessibilityLabel={`${t(serviceScheduleLabel[schedule])}, ${when}`}
            style={[styles.option, selected && styles.selected]}
          >
            <Text variant="cardTitle" tone={selected ? 'brand' : 'primary'} align="center">
              {t(serviceScheduleLabel[schedule])}
            </Text>
            <Text variant="caption" tone="secondary" align="center" numberOfLines={2}>
              {when}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
});

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.sm,
  },
  option: {
    // Two to a row: four across would squeeze the weekdays on a phone.
    flexGrow: 1,
    flexBasis: '40%',
    minWidth: 0,
    alignItems: 'center',
    gap: space.xxs,
    paddingVertical: space.md,
    paddingHorizontal: space.xs,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
  },
  selected: {
    borderWidth: 2,
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.selected,
  },
});
